'use client'

import {
    backfillClientPoints,
    FinancialSettings,
    getFinancialSettings,
    getResellerPlatformPercentage,
    updateFinancialSettings,
    updateResellerPlatformPercentage,
} from '@/lib/api/master/master-settings-api'
import { getPacks, Pack } from '@/lib/api/packs/packs-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdAttachMoney,
    MdInventory,
    MdPeople,
    MdSave,
    MdSettings,
    MdStars,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue) || 0
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

const formatCentsLabel = (cents: number): string => {
    return `R$ ${(cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

interface ResourcePricing {
    cost: string
    salePrice: string
}

export default function FinancialSettingsPage() {
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [savingPercentage, setSavingPercentage] = useState(false)
    const [savingPricing, setSavingPricing] = useState(false)
    const [percentage, setPercentage] = useState('')
    const [packs, setPacks] = useState<Pack[]>([])

    const [teleconsultation, setTeleconsultation] = useState<ResourcePricing>({
        cost: '',
        salePrice: '',
    })
    const [fiscalNote, setFiscalNote] = useState<ResourcePricing>({
        cost: '',
        salePrice: '',
    })
    const [signDocument, setSignDocument] = useState<ResourcePricing>({
        cost: '',
        salePrice: '',
    })
    const [storageGb, setStorageGb] = useState<ResourcePricing>({
        cost: '',
        salePrice: '',
    })
    // Multiplicador do preço mínimo de revenda sobre os preços de venda (ex: 5).
    // Valor do crédito e margem: o custo de um crédito não é fixo, é o
    // consumo real de IA, que varia por modelo (tela Consumo de IA).
    const [aiCreditSalePrice, setAiCreditSalePrice] = useState('')
    const [aiCreditMargin, setAiCreditMargin] = useState('')
    const [videoCreditSalePrice, setVideoCreditSalePrice] = useState('')
    const [videoViewerMinuteCredits, setVideoViewerMinuteCredits] = useState('')
    const [videoRecordingMinuteCredits, setVideoRecordingMinuteCredits] = useState('')
    const [resellerMultiplier, setResellerMultiplier] = useState('')
    const [extraStorePercentage, setExtraStorePercentage] = useState('')
    const [pointValue, setPointValue] = useState('')
    const [assigningPoints, setAssigningPoints] = useState(false)

    useEffect(() => {
        loadAll()
    }, [])

    const loadAll = async () => {
        setLoading(true)
        try {
            const [percRes, finRes, packsRes] = await Promise.all([
                getResellerPlatformPercentage(),
                getFinancialSettings(),
                getPacks(),
            ])

            if (percRes.success) {
                setPercentage(percRes.result.percentage?.toString() || '0')
            }

            if (finRes.success) {
                const s = finRes.result
                setTeleconsultation({
                    cost: formatCentsToDisplay(s.teleconsultationCostPerMinute),
                    salePrice: formatCentsToDisplay(
                        s.teleconsultationSalePricePerMinute
                    ),
                })
                setFiscalNote({
                    cost: formatCentsToDisplay(s.fiscalNoteCost),
                    salePrice: formatCentsToDisplay(s.fiscalNoteSalePrice),
                })
                setSignDocument({
                    cost: formatCentsToDisplay(s.signDocumentCost ?? 0),
                    salePrice: formatCentsToDisplay(
                        s.signDocumentSalePrice ?? 0
                    ),
                })
                setResellerMultiplier(
                    (s.resellerResourceMultiplier ?? 5).toString()
                )
                setStorageGb({
                    cost: formatCentsToDisplay(s.storageGbCost ?? 0),
                    salePrice: formatCentsToDisplay(s.storageGbSalePrice ?? 0),
                })
                setAiCreditSalePrice(formatCentsToDisplay(s.aiCreditSalePrice ?? 0))
                setAiCreditMargin((s.aiCreditMarginPercent ?? 0).toString())
                setVideoCreditSalePrice(formatCentsToDisplay(s.videoCreditSalePrice ?? 0))
                setVideoViewerMinuteCredits((s.videoViewerMinuteCredits ?? 0.5).toString())
                setVideoRecordingMinuteCredits((s.videoRecordingMinuteCredits ?? 2).toString())
                setExtraStorePercentage(
                    s.extraStorePercentage?.toString() || '0'
                )
                setPointValue(formatCentsToDisplay(s.pointValueCents ?? 1))
            }

            if (packsRes.success) {
                setPacks(packsRes.result)
            }
        } catch {
            toast.error('Erro ao carregar configurações')
        } finally {
            setLoading(false)
        }
    }

    const handleSaveResellerPercentage = async () => {
        const value = parseFloat(percentage)
        if (isNaN(value) || value < 0 || value > 100) {
            toast.error('Porcentagem deve ser um valor entre 0 e 100')
            return
        }

        setSavingPercentage(true)
        try {
            const response = await updateResellerPlatformPercentage(value)
            if (response.success) {
                toast.success('Porcentagem salva com sucesso')
            } else {
                toast.error(response.error || 'Erro ao salvar porcentagem')
            }
        } catch {
            toast.error('Erro ao salvar porcentagem')
        } finally {
            setSavingPercentage(false)
        }
    }

    const handleSavePricing = async () => {
        const data: FinancialSettings = {
            teleconsultationCostPerMinute: parseCurrencyToCents(
                teleconsultation.cost
            ),
            teleconsultationSalePricePerMinute: parseCurrencyToCents(
                teleconsultation.salePrice
            ),
            fiscalNoteCost: parseCurrencyToCents(fiscalNote.cost),
            fiscalNoteSalePrice: parseCurrencyToCents(fiscalNote.salePrice),
            signDocumentCost: parseCurrencyToCents(signDocument.cost),
            signDocumentSalePrice: parseCurrencyToCents(signDocument.salePrice),
            storageGbCost: parseCurrencyToCents(storageGb.cost),
            storageGbSalePrice: parseCurrencyToCents(storageGb.salePrice),
            aiCreditSalePrice: parseCurrencyToCents(aiCreditSalePrice),
            aiCreditMarginPercent: Math.min(95, Math.max(0, parseFloat(aiCreditMargin.replace(',', '.')) || 0)),
            videoCreditSalePrice: parseCurrencyToCents(videoCreditSalePrice),
            videoViewerMinuteCredits: Math.max(0, parseFloat(videoViewerMinuteCredits.replace(',', '.')) || 0),
            videoRecordingMinuteCredits: Math.max(0, parseFloat(videoRecordingMinuteCredits.replace(',', '.')) || 0),
            resellerResourceMultiplier: parseFloat(resellerMultiplier) || 0,
            extraStorePercentage: parseFloat(extraStorePercentage) || 0,
            // Valor de 1 ponto de fidelidade (mínimo 1 centavo).
            pointValueCents: Math.max(1, parseCurrencyToCents(pointValue)),
        }

        setSavingPricing(true)
        try {
            const response = await updateFinancialSettings(data)
            if (response.success) {
                toast.success('Configurações de preço salvas com sucesso')
            } else {
                toast.error(
                    response.error || 'Erro ao salvar configurações de preço'
                )
            }
        } catch {
            toast.error('Erro ao salvar configurações de preço')
        } finally {
            setSavingPricing(false)
        }
    }

    const handleAssignPoints = async () => {
        setAssigningPoints(true)
        try {
            const response = await backfillClientPoints()
            if (response.success) {
                const r = response.result
                toast.success(
                    `Pontos atribuídos: ${r.credited} cobrança(s) creditada(s), ${r.clients} cliente(s) atualizado(s) de ${r.processed} elegíveis`
                )
            } else {
                toast.error(response.error || 'Erro ao atribuir pontos')
            }
        } catch {
            toast.error('Erro ao atribuir pontos')
        } finally {
            setAssigningPoints(false)
        }
    }

    const handleCurrencyChange = (
        setter: React.Dispatch<React.SetStateAction<ResourcePricing>>,
        field: keyof ResourcePricing,
        value: string
    ) => {
        const formatted = currencyFormat(value)
        setter(prev => ({ ...prev, [field]: formatted }))
    }

    const calcMargin = (costStr: string, salePriceStr: string): string => {
        const cost = parseCurrencyToCents(costStr)
        const sale = parseCurrencyToCents(salePriceStr)
        if (sale === 0) return '-'
        if (cost === 0) return '100%'
        const margin = ((sale - cost) / sale) * 100
        return `${margin.toFixed(1)}%`
    }

    const calcPackPrice = (pack: Pack): number => {
        let unitPrice = 0
        if (pack.type === 'teleconsultation-minutes') {
            unitPrice = parseCurrencyToCents(teleconsultation.salePrice)
        } else if (pack.type === 'fiscal-notes') {
            unitPrice = parseCurrencyToCents(fiscalNote.salePrice)
        } else if (pack.type === 'ai-credits') {
            unitPrice = parseCurrencyToCents(aiCreditSalePrice)
        }
        if (unitPrice > 0) {
            return pack.quantity * unitPrice
        }
        return pack.price
    }

    const packTypeLabel = (type: string): string => {
        switch (type) {
            case 'teleconsultation-minutes':
                return 'Teleconsulta'
            case 'fiscal-notes':
                return 'Notas Fiscais'
            case 'ai-credits':
                return 'Créditos de IA'
            default:
                return type
        }
    }

    const examplePrice = 500000
    const pctValue = parseFloat(percentage) || 0
    const extraCost = Math.ceil((examplePrice * pctValue) / 100)

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <MdSettings className="text-xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">
                            Configurações Financeiras
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie custos, preços de venda e parâmetros de
                            cobrança
                        </p>
                    </div>
                </div>
            </div>

            {/* Card: Split de Receita por Parceiro */}
            <div className="card bg-base-100 shadow-sm border border-base-300">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                        <MdPeople className="text-xl text-secondary" />
                        <h2 className="card-title text-lg">
                            Split de Receita por Parceiro
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/70 mb-4">
                        Porcentagem da assinatura de cada parceiro que fica para
                        a plataforma. O restante é repassado ao cadastrante via
                        PIX no dia configurado.
                    </p>

                    <div className="form-control w-full max-w-xs">
                        <label className="label">
                            <span className="label-text font-medium">
                                Porcentagem da plataforma (%)
                            </span>
                        </label>
                        <div className="join">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={percentage}
                                onChange={e => setPercentage(e.target.value)}
                                className="input input-bordered join-item w-full"
                                placeholder="0"
                            />
                            <span className="join-item flex items-center px-4 bg-base-200 border border-base-300 font-medium">
                                %
                            </span>
                        </div>
                    </div>

                    {pctValue > 0 && (
                        <div className="mt-4 p-4 bg-info/10 rounded-lg border border-info/20">
                            <p className="text-sm font-medium text-info-content">
                                Exemplo de cálculo
                            </p>
                            <p className="text-sm text-base-content/70 mt-1">
                                Para uma assinatura de parceiro de R${' '}
                                {(examplePrice / 100)
                                    .toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })
                                    .replace('.', ',')}
                                , a plataforma retém{' '}
                                <strong>
                                    R${' '}
                                    {(extraCost / 100)
                                        .toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })
                                        .replace('.', ',')}
                                </strong>{' '}
                                ({pctValue}%) e o cadastrante recebe{' '}
                                <strong>
                                    R${' '}
                                    {((examplePrice - extraCost) / 100)
                                        .toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })
                                        .replace('.', ',')}
                                </strong>
                                .
                            </p>
                        </div>
                    )}

                    <div className="card-actions justify-end mt-6">
                        <button
                            onClick={handleSaveResellerPercentage}
                            disabled={savingPercentage}
                            className="btn btn-secondary gap-2"
                        >
                            {savingPercentage ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave className="text-lg" />
                            )}
                            Salvar Porcentagem
                        </button>
                    </div>
                </div>
            </div>

            {/* Card: Custos e Preços por Recurso */}
            <div className="card bg-base-100 shadow-sm border border-base-300">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                        <MdAttachMoney className="text-xl text-success" />
                        <h2 className="card-title text-lg">
                            Custos e Preços por Recurso
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/70 mb-6">
                        Defina o custo real (o que você paga) e o preço de venda
                        (o que você cobra) por unidade de cada recurso. Os
                        preços dos packs serão calculados automaticamente.
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Teleconsulta */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">
                                Teleconsulta
                            </h3>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Custo por minuto
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={teleconsultation.cost}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setTeleconsultation,
                                            'cost',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Preço de venda
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={teleconsultation.salePrice}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setTeleconsultation,
                                            'salePrice',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="text-xs text-base-content/50">
                                Margem:{' '}
                                <span className="font-medium">
                                    {calcMargin(
                                        teleconsultation.cost,
                                        teleconsultation.salePrice
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Notas Fiscais */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">
                                Notas Fiscais
                            </h3>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Custo por nota fiscal
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={fiscalNote.cost}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setFiscalNote,
                                            'cost',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Preço de venda
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={fiscalNote.salePrice}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setFiscalNote,
                                            'salePrice',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="text-xs text-base-content/50">
                                Margem:{' '}
                                <span className="font-medium">
                                    {calcMargin(
                                        fiscalNote.cost,
                                        fiscalNote.salePrice
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Documentos de Assinatura */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">
                                Documentos de Assinatura
                            </h3>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Custo por documento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={signDocument.cost}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setSignDocument,
                                            'cost',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Preço de venda
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={signDocument.salePrice}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setSignDocument,
                                            'salePrice',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="text-xs text-base-content/50">
                                Margem:{' '}
                                <span className="font-medium">
                                    {calcMargin(
                                        signDocument.cost,
                                        signDocument.salePrice
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Armazenamento — cobrado por GB/mês enquanto a
                            recorrência estiver ativa, e não por evento. */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">
                                Armazenamento (GB extra)
                            </h3>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Custo por GB/mês
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={storageGb.cost}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setStorageGb,
                                            'cost',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Preço de venda
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={storageGb.salePrice}
                                    onChange={e =>
                                        handleCurrencyChange(
                                            setStorageGb,
                                            'salePrice',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>
                            <div className="text-xs text-base-content/50">
                                Margem:{' '}
                                <span className="font-medium">
                                    {calcMargin(
                                        storageGb.cost,
                                        storageGb.salePrice
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Créditos de IA — vendidos na Loja do Sistema. O custo
                            não é fixo: é o consumo real de cada chamada. */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">
                                Créditos de IA
                            </h3>
                            <div className="form-control w-full">
                                <label className="label whitespace-normal" htmlFor="ai-credit-sale-price">
                                    <span className="label-text text-sm whitespace-normal leading-snug">
                                        Valor de um crédito
                                    </span>
                                </label>
                                <input
                                    id="ai-credit-sale-price"
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={aiCreditSalePrice}
                                    onChange={e =>
                                        setAiCreditSalePrice(
                                            currencyFormat(e.target.value)
                                        )
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label className="label whitespace-normal" htmlFor="ai-credit-margin">
                                    <span className="label-text text-sm whitespace-normal leading-snug">
                                        Margem sobre o custo da IA (%)
                                    </span>
                                </label>
                                <input
                                    id="ai-credit-margin"
                                    type="number"
                                    min={0}
                                    max={95}
                                    step="0.1"
                                    placeholder="50"
                                    className="input input-bordered w-full input-sm"
                                    value={aiCreditMargin}
                                    onChange={e => setAiCreditMargin(e.target.value)}
                                />
                            </div>
                            {(() => {
                                const price = parseCurrencyToCents(aiCreditSalePrice)
                                const margin = Math.min(95, Math.max(0, parseFloat(aiCreditMargin.replace(',', '.')) || 0))
                                if (price <= 0) return null
                                // Exemplo fixo: R$ 0,01 de custo real de IA.
                                const credits = 1 / (1 - margin / 100) / price
                                return (
                                    <p className="text-xs text-base-content/60">
                                        Cada R$ 0,01 de custo real de IA desconta{' '}
                                        <span className="font-medium">
                                            {credits.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} crédito(s)
                                        </span>
                                        . A margem acompanha mudança de preço da
                                        OpenAI e troca de modelo.
                                    </p>
                                )
                            })()}
                            <p className="text-xs text-base-content/50">
                                Valor zero tira o crédito da Loja. Mantenha zero até o
                                consumo de IA passar a debitar créditos: antes
                                disso, o crédito comprado não é usado.
                            </p>
                        </div>

                        {/* Créditos de vídeo (MediarMeet) — vendidos na Loja do Sistema. */}
                        <div className="p-4 rounded-xl border border-base-300 bg-base-50 space-y-4">
                            <h3 className="font-bold text-base-content">Créditos de vídeo</h3>
                            <div className="form-control w-full">
                                <label className="label whitespace-normal" htmlFor="video-credit-sale-price">
                                    <span className="label-text text-sm whitespace-normal leading-snug">
                                        Valor de um crédito (1 minuto por participante com vídeo)
                                    </span>
                                </label>
                                <input
                                    id="video-credit-sale-price"
                                    type="text"
                                    placeholder="R$ 0,00"
                                    className="input input-bordered w-full input-sm"
                                    value={videoCreditSalePrice}
                                    onChange={e => setVideoCreditSalePrice(currencyFormat(e.target.value))}
                                />
                            </div>
                            <div className="space-y-4">
                                <div className="form-control w-full">
                                    <label className="label whitespace-normal" htmlFor="video-viewer-minute">
                                        <span className="label-text text-sm whitespace-normal leading-snug">Créditos por minuto de espectador</span>
                                    </label>
                                    <input
                                        id="video-viewer-minute"
                                        type="number"
                                        min={0}
                                        step="0.05"
                                        placeholder="0,5"
                                        className="input input-bordered w-full input-sm"
                                        value={videoViewerMinuteCredits}
                                        onChange={e => setVideoViewerMinuteCredits(e.target.value)}
                                    />
                                </div>
                                <div className="form-control w-full">
                                    <label className="label whitespace-normal" htmlFor="video-recording-minute">
                                        <span className="label-text text-sm whitespace-normal leading-snug">Créditos por minuto gravado</span>
                                    </label>
                                    <input
                                        id="video-recording-minute"
                                        type="number"
                                        min={0}
                                        step="0.1"
                                        placeholder="2"
                                        className="input input-bordered w-full input-sm"
                                        value={videoRecordingMinuteCredits}
                                        onChange={e => setVideoRecordingMinuteCredits(e.target.value)}
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-base-content/50">
                                Valor zero desliga a cobrança de vídeo: nada é debitado nem bloqueado, e o crédito
                                sai da Loja. Sem saldo, a reunião em andamento nunca é derrubada; só novas salas
                                ficam bloqueadas até a recarga.
                            </p>
                        </div>
                    </div>

                    {/* Multiplicador do preço mínimo de revenda */}
                    <div className="mt-6 p-4 rounded-xl border border-base-300 bg-base-50">
                        <h3 className="font-bold text-base-content mb-1">
                            Multiplicador de Revenda
                        </h3>
                        <p className="text-xs text-base-content/60 mb-3">
                            O valor mínimo de um plano de revenda é o preço de
                            venda de cada recurso alocado multiplicado por este
                            fator (ex: 5 = 5×). O revendedor não pode precificar
                            abaixo disso.
                        </p>
                        <div className="form-control w-full max-w-xs">
                            <label className="label">
                                <span className="label-text text-sm">
                                    Multiplicador (×)
                                </span>
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                placeholder="Ex: 5"
                                className="input input-bordered w-full input-sm"
                                value={resellerMultiplier}
                                onChange={e =>
                                    setResellerMultiplier(e.target.value)
                                }
                            />
                        </div>
                    </div>

                    {/* Programa de Fidelidade */}
                    <div className="mt-6 p-4 rounded-xl border border-base-300 bg-base-50">
                        <h3 className="font-bold text-base-content mb-1">
                            Programa de Fidelidade
                        </h3>
                        <p className="text-xs text-base-content/60 mb-3">
                            Clientes acumulam 1 ponto por R$ 1,00 pago. Defina
                            abaixo quanto vale 1 ponto (usado no resgate).
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                            <div className="form-control w-full max-w-xs">
                                <label className="label">
                                    <span className="label-text text-sm">
                                        Valor de 1 ponto
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="R$ 0,01"
                                    className="input input-bordered w-full input-sm"
                                    value={pointValue}
                                    onChange={e =>
                                        setPointValue(
                                            currencyFormat(e.target.value)
                                        )
                                    }
                                />
                                <span className="text-xs text-base-content/50 mt-1">
                                    Mínimo R$ 0,01 por ponto.
                                </span>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-base-300">
                            <button
                                type="button"
                                onClick={handleAssignPoints}
                                disabled={assigningPoints}
                                className="btn btn-outline btn-sm gap-2"
                                title="Atribui retroativamente os pontos das cobranças já pagas. Pode rodar mais de uma vez sem duplicar."
                            >
                                {assigningPoints ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdStars className="text-lg" />
                                )}
                                Atribuir pontos retroativos
                            </button>
                        </div>
                        <p className="text-xs text-base-content/50 mt-2">
                            Atribui os pontos das cobranças já pagas (1 ponto
                            por R$ 1,00). Operação idempotente — cobranças já
                            creditadas são ignoradas.
                        </p>
                    </div>

                    <div className="card-actions justify-end mt-6">
                        <button
                            onClick={handleSavePricing}
                            disabled={savingPricing}
                            className="btn btn-secondary gap-2"
                        >
                            {savingPricing ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave className="text-lg" />
                            )}
                            Salvar Preços
                        </button>
                    </div>
                </div>
            </div>

            {/* Card: Lojas Online Extras */}
            <div className="card bg-base-100 shadow-sm border border-base-300">
                <div className="card-body">
                    <div className="flex items-center gap-2 mb-2">
                        <MdSettings className="text-xl text-secondary" />
                        <h2 className="card-title text-lg">
                            Lojas Online Extras
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/60 mb-4">
                        Porcentagem cobrada sobre o valor da assinatura para
                        cada loja extra além do limite incluso no plano.
                    </p>

                    <div className="form-control w-full max-w-xs">
                        <label className="label">
                            <span className="label-text font-medium">
                                Porcentagem por loja extra
                            </span>
                        </label>
                        <label className="input input-bordered flex items-center gap-2">
                            <input
                                type="number"
                                className="grow"
                                placeholder="20"
                                min={0}
                                max={100}
                                step={1}
                                value={extraStorePercentage}
                                onChange={e =>
                                    setExtraStorePercentage(e.target.value)
                                }
                            />
                            <span className="text-base-content/50">%</span>
                        </label>
                        <label className="label">
                            <span className="label-text-alt text-base-content/50">
                                Ex: 20% significa que uma assinatura de R$
                                100,00 passará para R$ 120,00 por loja extra
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Card: Previsão de Preços dos Packs */}
            {packs.length > 0 && (
                <div className="card bg-base-100 shadow-sm border border-base-300">
                    <div className="card-body">
                        <div className="flex items-center gap-2 mb-2">
                            <MdInventory className="text-xl text-warning" />
                            <h2 className="card-title text-lg">
                                Previsão de Preços dos Packs
                            </h2>
                        </div>
                        <p className="text-sm text-base-content/70 mb-4">
                            Os preços dos packs são calculados automaticamente
                            com base no preço de venda por unidade configurado
                            acima. Se nenhum preço de venda for definido, o
                            preço padrão do pack será utilizado.
                        </p>

                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Pack</th>
                                        <th>Tipo</th>
                                        <th className="text-right">
                                            Quantidade
                                        </th>
                                        <th className="text-right">
                                            Preço Unit.
                                        </th>
                                        <th className="text-right">
                                            Preço Total
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {packs.map(pack => {
                                        const totalPrice = calcPackPrice(pack)
                                        const unitPrice = Math.round(
                                            totalPrice / pack.quantity
                                        )
                                        return (
                                            <tr key={pack.code}>
                                                <td className="font-medium">
                                                    {pack.name}
                                                </td>
                                                <td>
                                                    <span className="badge badge-ghost badge-sm">
                                                        {packTypeLabel(
                                                            pack.type
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="text-right">
                                                    {pack.quantity.toLocaleString(
                                                        'pt-BR'
                                                    )}
                                                </td>
                                                <td className="text-right text-base-content/60">
                                                    {formatCentsLabel(
                                                        unitPrice
                                                    )}
                                                </td>
                                                <td className="text-right font-medium">
                                                    {formatCentsLabel(
                                                        totalPrice
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
